import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import api, { agentServiceApi } from '../../../api/axios';

const CreateKnowledgeBaseModal = ({ isOpen, onClose, availableAgents }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    link: '',
    document: null,
    usedByAgents: []
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const typeOptions = [
    { value: 'Link', label: 'Link' },
    { value: 'Doc', label: 'Doc' }
  ];

  const agentOptions =
    availableAgents?.map(agent => ({
      value: agent?.id,
      label: agent?.name
    })) || [];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      type: value,
      link: '',
      document: null
    }));
  };

  const handleAgentsChange = (value) => {
    setFormData(prev => ({ ...prev, usedByAgents: value }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, document: file }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;

    try {
      setLoading(true);

      const agentIds = formData.usedByAgents.join(',');

      if (formData.type === 'Link') {
        // 🔗 LINK KB
        await api.post(
          `/api/v1/dashboard/create-knowledge-base`,
          null,
          {
            params: {
              knowledgeBaseType: 'LINK',
              knowledgeBaseName: formData.name,
              url: formData.link,
              agentIds
            }
          }
        );
      } else {
        // 📄 DOC KB — upload via presigned URL (same flow as agent creation)
        const presignRes = await agentServiceApi.post('/storage/presigned-url', {
          file_name: formData.document.name,
          section: 'knowledge_base',
        });
        const { upload_url, public_url } = presignRes.data;

        await fetch(upload_url, {
          method: 'PUT',
          body: formData.document,
          headers: { 'Content-Type': formData.document.type || 'application/octet-stream' },
        });

        await api.post(
          `/api/v1/dashboard/create-knowledge-base`,
          null,
          {
            params: {
              knowledgeBaseType: 'DOC',
              knowledgeBaseName: formData.name,
              url: public_url,
              agentIds
            }
          }
        );
      }

      // ✅ Success UI (unchanged)
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          name: '',
          type: '',
          link: '',
          document: null,
          usedByAgents: []
        });
        onClose();
        window.location.reload(); // ✅ refresh page
      }, 3000);

    } catch (error) {
      console.error('Create Knowledge Base failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const isValidInput =
    formData?.name &&
    formData?.type &&
    ((formData?.type === 'Link' && formData?.link) ||
      (formData?.type === 'Doc' && formData?.document)) &&
    formData?.usedByAgents?.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            Create Knowledge Base
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            type="button"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="m-4 md:m-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm font-medium text-success">
              Knowledge Base Created Successfully
            </p>
          </div>
        )}

        {/* Form */}
        {!showSuccess && (
          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Type <span className="text-error">*</span>
              </label>
              <Select
                options={typeOptions}
                value={formData.type}
                onChange={handleTypeChange}
                placeholder="Select type"
              />
            </div>

            {/* Link */}
            {formData.type === 'Link' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Add Link <span className="text-error">*</span>
                </label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
                />
              </div>
            )}

            {/* Document */}
            {formData.type === 'Doc' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  Upload Document <span className="text-error">*</span>
                </label>
                <input
                  type="file"
                  accept=".docx,.pdf"
                  onChange={handleFileChange}
                  required
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
                />
              </div>
            )}

            {/* Agents */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Used By Agent(s) <span className="text-error">*</span>
              </label>
              <Select
                options={agentOptions}
                value={formData.usedByAgents}
                onChange={handleAgentsChange}
                multiple
                searchable
              />
            </div>

            <Button
              type="submit"
              fullWidth
              disabled={!isValidInput || loading}
            >
              {loading ? 'Creating...' : 'Create Knowledge Base'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateKnowledgeBaseModal;
